// ==================== PAGINATION COMPONENT ====================

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ChevronDown, Check } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../utils/helpers';
import { APP_CONSTANTS } from '../../constants';

// ==================== TYPES ====================

export interface PaginationProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items */
  totalItems: number;
  /** Current page size */
  pageSize: number;
  /** Whether pagination is in loading state */
  isLoading?: boolean;
  /** Available page size options (defaults to APP_CONSTANTS.PAGE_SIZE_OPTIONS) */
  pageSizeOptions?: number[];
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;
  /** Label for items (e.g., "customers", "items", "records") */
  itemLabel?: string;
  /** Show page size selector */
  showPageSizeSelector?: boolean;
  /** Show page info text */
  showPageInfo?: boolean;
  /** Custom className */
  className?: string;
}

// ==================== PAGE SIZE DROPDOWN COMPONENT ====================

interface PageSizeDropdownProps {
  value: number;
  options: number[];
  onChange: (size: number) => void;
  disabled?: boolean;
}

const PageSizeDropdown = ({ value, options, onChange, disabled }: PageSizeDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
          'bg-(--color-navy-light) border border-(--color-border-gray)',
          'hover:border-(--color-signal-orange)/50',
          'focus:outline-none focus:ring-2 focus:ring-(--color-signal-orange)/20 focus:border-(--color-signal-orange)',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className="text-white">{value}</span>
        <ChevronDown 
          size={14} 
          className={cn(
            'text-(--color-muted-gray) transition-transform',
            isOpen && 'rotate-180'
          )} 
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute left-0 bottom-full mb-1 w-24 py-1 bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-lg shadow-xl z-50"
            >
              {options.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'flex items-center justify-between w-full px-3 py-2 text-sm transition-colors',
                    value === option
                      ? 'bg-(--color-signal-orange)/10 text-(--color-signal-orange)'
                      : 'text-(--color-soft-gray) hover:bg-white/5 hover:text-white'
                  )}
                >
                  <span>{option}</span>
                  {value === option && <Check size={14} />}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== MAIN PAGINATION COMPONENT ====================

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  isLoading = false,
  pageSizeOptions = [...APP_CONSTANTS.PAGE_SIZE_OPTIONS] as number[],
  onPageChange,
  onPageSizeChange,
  itemLabel = 'items',
  showPageSizeSelector = true,
  showPageInfo = true,
  className,
}: PaginationProps) => {
  // Calculate display range
  const startItem = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    
    for (let i = 1; i <= totalPages; i++) {
      // Always show first page, last page, current page, and pages around current
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== 'ellipsis') {
        pages.push('ellipsis');
      }
    }
    
    return pages;
  };

  // Don't render if no items
  if (totalItems === 0 && totalPages <= 1) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row items-center justify-between gap-4 px-5 py-4 border-t border-(--color-border-gray)/50',
        className
      )}
    >
      {/* Left side: Page size selector and info */}
      <div className="flex items-center gap-4">
        {/* Page size selector */}
        {showPageSizeSelector && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-(--color-muted-gray)">Show</span>
            <PageSizeDropdown
              value={pageSize}
              options={pageSizeOptions}
              onChange={onPageSizeChange}
              disabled={isLoading}
            />
            <span className="text-sm text-(--color-muted-gray)">per page</span>
          </div>
        )}

        {/* Page info */}
        {showPageInfo && (
          <div className="hidden sm:block text-sm text-(--color-muted-gray)">
            Showing <span className="text-(--color-soft-gray) font-medium">{startItem}</span> to{' '}
            <span className="text-(--color-soft-gray) font-medium">{endItem}</span> of{' '}
            <span className="text-(--color-soft-gray) font-medium">{totalItems}</span> {itemLabel}
          </div>
        )}
      </div>

      {/* Right side: Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          {/* Previous button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
            className="p-2 text-(--color-muted-gray) hover:text-white"
          >
            <ChevronLeft size={18} />
          </Button>

          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {getPageNumbers().map((page, index) => {
              if (page === 'ellipsis') {
                return (
                  <span key={`ellipsis-${index}`} className="px-2 text-(--color-muted-gray)">
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  disabled={isLoading}
                  className={cn(
                    'min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-all',
                    currentPage === page
                      ? 'bg-(--color-signal-orange) text-white shadow-sm shadow-(--color-signal-orange)/25'
                      : 'text-(--color-soft-gray) hover:bg-white/5 hover:text-white'
                  )}
                >
                  {page}
                </button>
              );
            })}
          </div>

          {/* Next button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
            className="p-2 text-(--color-muted-gray) hover:text-white"
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      )}
    </div>
  );
};

export { Pagination };

